import { Link } from 'react-router-dom'
import { Receipt, Trophy } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import {
  commissionStatusVariant,
  formatCommissionStatusLabel,
  type CommissionsTabView,
} from '@/components/Dashboard/commissionsTab.utils'
import {
  formatDashboardCurrency,
  formatDashboardNumber,
  formatDashboardPercent,
} from '@/utils/crmValuesDisplay'
type Props = {
  view: CommissionsTabView
  valuesVisible?: boolean
}

export function CommissionsRecentTable({ view, valuesVisible = false }: Props) {
  return (
    <div className='grid grid-cols-1 gap-4 xl:grid-cols-3'>
      <div className='xl:col-span-2 rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] shadow-sm'>
        <div className='flex flex-wrap items-end justify-between gap-3 border-b border-[var(--border)] px-5 py-4'>
          <div>
            <div className='flex items-center gap-2'>
              <Receipt size={16} className='text-accent' aria-hidden />
              <h3 className='text-base font-semibold text-text-primary'>Comissões no período</h3>
            </div>
            <p className='mt-1 text-sm text-text-muted'>
              Deals ganhos com comissão gerada — visão consolidada para gestão
            </p>
          </div>
        </div>

        {!view.recentCommissions.length ? (
          <p className='px-5 py-10 text-center text-sm text-text-muted'>
            Nenhuma comissão registrada para deals fechados no período selecionado.
          </p>
        ) : (
          <div className='overflow-x-auto'>
            <table className='min-w-full text-left text-sm'>
              <thead>
                <tr className='border-b border-[var(--border)] text-xs uppercase tracking-wide text-text-muted'>
                  <th className='px-5 py-3 font-semibold'>Negócio</th>
                  <th className='px-3 py-3 font-semibold'>Closer</th>
                  <th className='px-3 py-3 font-semibold'>Valor deal</th>
                  <th className='px-3 py-3 font-semibold'>Comissão</th>
                  <th className='px-5 py-3 font-semibold'>Status</th>
                </tr>
              </thead>
              <tbody>
                {view.recentCommissions.map((row) => (
                  <tr
                    key={row.id}
                    className='border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--bg-dark)]/10'
                  >
                    <td className='px-5 py-3'>
                      <p className='font-medium text-text-primary'>{row.dealTitle}</p>
                      <p className='text-xs text-text-muted'>{row.empresa}</p>
                    </td>
                    <td className='px-3 py-3 text-text-secondary'>{row.sellerName}</td>
                    <td className='px-3 py-3 tabular-nums text-text-secondary'>
                      {formatDashboardCurrency(row.dealValue, valuesVisible)}
                      <span className='ml-1 text-xs text-text-muted'>
                        ({formatDashboardPercent(row.rate, valuesVisible)})
                      </span>
                    </td>
                    <td className='px-3 py-3 font-semibold tabular-nums text-text-primary'>
                      {formatDashboardCurrency(row.commissionValue, valuesVisible)}
                    </td>
                    <td className='px-5 py-3'>
                      <Badge variant={commissionStatusVariant(row.status)}>
                        {formatCommissionStatusLabel(row.status)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <aside className='rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] p-5 shadow-sm'>
        <div className='flex items-center gap-2'>
          <Trophy size={16} className='text-amber-500' aria-hidden />
          <h3 className='text-base font-semibold text-text-primary'>Por closer</h3>
        </div>
        <p className='mt-1 text-sm text-text-muted'>Ranking de volume no período</p>

        {!view.topBeneficiaries.length ? (
          <p className='mt-6 text-sm text-text-muted'>Sem movimento no período.</p>
        ) : (
          <ul className='mt-4 space-y-3'>
            {view.topBeneficiaries.map((row, index) => (
              <li
                key={row.userId}
                className='flex items-center justify-between gap-3 border-b border-[var(--border)] pb-3 last:border-b-0 last:pb-0'
              >
                <div className='min-w-0'>
                  <p className='truncate font-medium text-text-primary'>
                    {index + 1}. {row.name}
                  </p>
                  <p className='text-xs text-text-muted'>
                    {formatDashboardNumber(row.count, valuesVisible)} comissões
                  </p>
                </div>
                <p className='shrink-0 font-semibold tabular-nums text-text-primary'>
                  {formatDashboardCurrency(row.totalValue, valuesVisible)}
                </p>
              </li>
            ))}
          </ul>
        )}

        <div className='mt-5 border-t border-[var(--border)] pt-4'>
          <p className='text-xs text-text-muted'>
            Pagamentos e operação detalhada ficam no módulo financeiro.
          </p>
          <Link
            to='/financeiro'
            className='mt-2 inline-flex text-xs font-semibold text-[var(--brand-primary)] hover:underline'
          >
            Abrir módulo financeiro
          </Link>
        </div>
      </aside>
    </div>
  )
}
